const yagaFloatingWidgetSelectors = [
  '#sj_l_widget_button',
  '#tidio-chat',
  '#klaviyo-onsite',
  '[data-testid*="teaser" i]',
  '[data-testid*="launcher" i]',
  '[aria-label*="klaviyo" i]',
  '[class*="klaviyo-form"]',
].join(',');

let yagaFloatingWidgetObserver;
let yagaCartDrawerIsOpen = false;
let yagaCartDrawerContentObserver;

function moveYagaCartDrawerContentIntoScroller(cartDrawer) {
  const drawerInner = cartDrawer?.querySelector('.drawer__inner');
  const scrollContent = drawerInner?.querySelector(':scope > .drawer__scroll-content');
  if (!drawerInner || !scrollContent) return;

  const fixedDrawerSections = new Set([
    drawerInner.querySelector(':scope > .drawer__header'),
    scrollContent,
    drawerInner.querySelector(':scope > .drawer__footer'),
    drawerInner.querySelector(':scope > .drawer__inner-empty'),
  ]);
  const injectedContent = Array.from(drawerInner.children).filter(
    (element) =>
      !fixedDrawerSections.has(element) &&
      !['SCRIPT', 'STYLE', 'TEMPLATE'].includes(element.tagName)
  );

  if (!injectedContent.length) return;

  const firstOriginalChild = scrollContent.firstChild;
  injectedContent.forEach((element) => scrollContent.insertBefore(element, firstOriginalChild));
}

function observeYagaCartDrawerContent(cartDrawer) {
  const drawerInner = cartDrawer?.querySelector('.drawer__inner');
  if (!drawerInner) return;

  if (yagaCartDrawerContentObserver?.yagaTarget !== drawerInner) {
    yagaCartDrawerContentObserver?.disconnect();
    yagaCartDrawerContentObserver = new MutationObserver(() => {
      window.requestAnimationFrame(() => moveYagaCartDrawerContentIntoScroller(cartDrawer));
    });
    yagaCartDrawerContentObserver.yagaTarget = drawerInner;
    yagaCartDrawerContentObserver.observe(drawerInner, { childList: true });
  }

  moveYagaCartDrawerContentIntoScroller(cartDrawer);
}

function isYagaFixedWidget(element) {
  return element instanceof HTMLElement && window.getComputedStyle(element).position === 'fixed';
}

function getYagaFloatingWidgetRoot(element) {
  let currentElement = element;

  while (currentElement && currentElement !== document.body) {
    if (isYagaFixedWidget(currentElement)) return currentElement;
    currentElement = currentElement.parentElement;
  }

  return Array.from(element.querySelectorAll('*')).find(isYagaFixedWidget) || null;
}

function hideYagaFloatingWidgets() {
  document.querySelectorAll(yagaFloatingWidgetSelectors).forEach((element) => {
    const widgetRoot = getYagaFloatingWidgetRoot(element);
    if (widgetRoot) widgetRoot.setAttribute('data-yaga-cart-widget-hidden', 'true');
  });
}

function setYagaFloatingWidgetsHidden(shouldHide) {
  yagaCartDrawerIsOpen = shouldHide;

  if (!document.getElementById('YagaCartWidgetVisibility')) {
    const style = document.createElement('style');
    style.id = 'YagaCartWidgetVisibility';
    style.textContent = `
      [data-yaga-cart-widget-hidden="true"] {
        opacity: 0 !important;
        visibility: hidden !important;
        pointer-events: none !important;
      }
    `;
    document.head.appendChild(style);
  }

  if (shouldHide) {
    hideYagaFloatingWidgets();

    if (!yagaFloatingWidgetObserver) {
      yagaFloatingWidgetObserver = new MutationObserver(() => {
        if (yagaCartDrawerIsOpen) hideYagaFloatingWidgets();
      });
      yagaFloatingWidgetObserver.observe(document.body, { childList: true, subtree: true });
    }
    return;
  }

  yagaFloatingWidgetObserver?.disconnect();
  yagaFloatingWidgetObserver = undefined;
  document.querySelectorAll('[data-yaga-cart-widget-hidden="true"]').forEach((element) => {
    element.removeAttribute('data-yaga-cart-widget-hidden');
  });
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    observeYagaCartDrawerContent(this);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    setYagaFloatingWidgetsHidden(true);
    observeYagaCartDrawerContent(this);
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    setYagaFloatingWidgetsHidden(false);
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    observeYagaCartDrawerContent(this);

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
